import { useEffect, useRef, useState } from 'react'
import { Stack, TextField, Typography, Checkbox, Button, useTheme, Grid } from '@mui/material'
import CheckCircleOutlineRoundedIcon from '@mui/icons-material/CheckCircleOutlineRounded'
import { useTranslation } from 'react-i18next'
import { useAppDispatch, useAppSelector } from '../../../store/hooks'
import { fsColors } from '../../../shared/constants'
import FsContainer from '../common/Container'
import FsContactCard from '../common/ContactCard'
import FsAlert, { FsAlertRef } from '../../common/atoms/Alert'
import { useLoading, useDebounce } from '../../../shared/hooks'
import ACTIONS from '../../../store/constants'
import {
  getEtlFinalCheckErrors,
  goBackETLStep,
  reportEtlErrors,
  updateEtlError,
  updateEtlStep,
} from '../../../store/actions'
import { EtlFinalCheckError } from '../../../shared/model/models'

const FsErrorRow = ({
  error,
  isChecked,
  onChange,
}: {
  error: EtlFinalCheckError
  isChecked: boolean
  onChange: (id: number, checked: boolean) => void
}) => {
  const theme = useTheme()
  const dispatch = useAppDispatch()
  const [uiError, setUIError] = useState<EtlFinalCheckError>(error)

  const debouncedUpdateErrorBE = useDebounce(() => {
    dispatch(updateEtlError({ error: uiError }))
  })

  return (
    <Grid
      container
      key={`error_${error.id}`}
      p={0.5}
      alignItems="center"
      justifyContent="space-between"
      sx={{
        backgroundColor: isChecked
          ? fsColors.fsGray.colors[2]
          : theme.palette.action.disabledBackground,
        borderRadius: 2,
      }}
    >
      <Grid size={{xs: 12, sm: 3}} container alignItems="center">
        <Checkbox
          size="small"
          checked={isChecked}
          disabled={error.report}
          onChange={(e) => onChange(error.id, e.target.checked)}
        />
        <Typography variant="body2" ml={0.5}>
          {error.column}
        </Typography>
      </Grid>
      <Grid size={{sm: 1}}>
        <Typography variant="body2">{error.row}</Typography>
      </Grid>
      <Grid size={{sm: 2}}>
        <Typography variant="body2">{error.error}</Typography>
      </Grid>
      <Grid size={{sm: 5.5}}>
        <TextField
          size="small"
          sx={{ backgroundColor: !error.report ? theme.palette.background.paper : '' }}
          fullWidth
          disabled={error.report}
          onChange={(e) => {
            setUIError({ ...error, comment: e.target.value })
            debouncedUpdateErrorBE()
          }}
          value={uiError.comment}
        />
      </Grid>
      <Grid size={{sm: 0.5}} container alignItems="center" justifyContent="flex-end" pr={1}>
        <CheckCircleOutlineRoundedIcon
          fontSize="small"
          sx={{
            color: error.report ? theme.palette.success.light : fsColors.fsGray.colors[1],
          }}
        />
      </Grid>
    </Grid>
  )
}

const FsFinalCheck = ({ setIsLoading }: { setIsLoading: (isLoading: boolean) => void }) => {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const alertRef = useRef<FsAlertRef>(null)
  const [isChecked, setIsChecked] = useState<number[]>([])
  const { etlFinalCheckErrors, latestEtlStatus } = useAppSelector((state) => state.currentEtl)

  useEffect(() => {
    if (!etlFinalCheckErrors) {
      dispatch(getEtlFinalCheckErrors({ reported: false }))
    }
  }, [latestEtlStatus, etlFinalCheckErrors])

  const handleReportError = async () => {
    try {
      const reportErrors = etlFinalCheckErrors?.map((errorToReport) =>
        isChecked.includes(errorToReport.id) ? { ...errorToReport, report: true } : errorToReport,
      )
      if (reportErrors) {
        await dispatch(reportEtlErrors({ errors: reportErrors }))
        setIsChecked([])
        alertRef.current?.openAlert({
          severity: 'success',
          message: t('errors.reported.sent'),
        })
      }
    } catch {
      alertRef.current?.openAlert({
        severity: 'error',
        message: t('something.went.wrong'),
      })
    }
  }

  const handleChangeCheckbox = (id: number, checked: boolean) => {
    setIsChecked(checked ? [...isChecked, id] : isChecked.filter((eId) => eId !== id))
  }

  const unReportedErrors = etlFinalCheckErrors?.filter((e) => !e.report)

  const { isFetching } = useLoading([
    ACTIONS.ETL.GET_FINAL_ERRORS,
    ACTIONS.ETL.REPORT_ETL_ERRORS,
    ACTIONS.ETL.UPDATE_ETL_STEP,
  ])

  useEffect(() => {
    setIsLoading(isFetching)
  }, [isFetching])

  return (
    <FsContainer
      footer={
        <Stack width="100%" direction="row" justifyContent="space-between">
          <Stack>
            <Button onClick={() => dispatch(goBackETLStep())}>{t('back')}</Button>
          </Stack>
          <Stack direction="row" gap={1}>
            <Button
              variant="outlined"
              onClick={() => dispatch(updateEtlStep({ step: 'FinalCheckDone' }))}
            >
              {unReportedErrors?.length === 0 ? t('continue') : t('ignore.continue')}
            </Button>
            <Button
              onClick={handleReportError}
              variant="contained"
              disabled={isChecked.length === 0}
            >
              {t('report.error', { number: isChecked.length })}
            </Button>
          </Stack>
        </Stack>
      }
    >
      {etlFinalCheckErrors?.length !== 0 ? (
        <Stack alignItems="flex-start" mt={2} gap={2}>
          <FsContactCard />
          {unReportedErrors?.length === 0 && (
            <Typography mt={2} variant="body1" ml={2}>
              {t('errors.reported')}
            </Typography>
          )}
          <Stack gap={1} width="100%">
            <Grid container p={0.5} alignItems="center" justifyContent="space-between">
              <Grid size={{xs: 12, sm: 3}} container alignItems="center">
                <Checkbox
                  size="small"
                  checked={
                    unReportedErrors?.length !== 0 && isChecked.length === unReportedErrors?.length
                  }
                  onChange={(e) => {
                    if (!e.target.checked) setIsChecked([])
                    else
                      setIsChecked(
                        etlFinalCheckErrors?.filter((er) => !er.report).map((err) => err.id) ?? [],
                      )
                  }}
                  disabled={unReportedErrors?.length === 0}
                />
                <Typography variant="h6">{t('column')}</Typography>
              </Grid>
              <Grid size={{sm: 1}}>
                <Typography variant="h6">{t('row')}</Typography>
              </Grid>
              <Grid size={{sm: 2}}>
                <Typography variant="h6">{t('error')}</Typography>
              </Grid>
              <Grid size={{sm: 5.5}}>
                <Typography variant="h6">{t('comment')}</Typography>
              </Grid>
              <Grid size={{sm: 0.5}} />
            </Grid>
            {etlFinalCheckErrors?.map((error) => (
              <FsErrorRow
                error={error}
                key={`error_${error.id}`}
                isChecked={isChecked.includes(error.id)}
                onChange={handleChangeCheckbox}
              />
            ))}
          </Stack>
        </Stack>
      ) : (
        <Typography mt={4} mb={8} variant="h6">
          {t('no.error')}
        </Typography>
      )}
      <FsAlert ref={alertRef} />
    </FsContainer>
  )
}

export default FsFinalCheck
